/**
 * Itinerary Catalog + Itinerary Requests API Service
 *
 * Two org-scoped surfaces plus RosterPilot's cross-org staff queue:
 *  - `/api/itinerary-catalog`  — browse published tournament itineraries and add one to your calendar
 *  - `/api/itinerary-requests` — ask RosterPilot to build one the catalog does not carry
 *  - `/api/admin/itinerary-requests` — system_admin only; the queue, decline and fulfil
 *
 * Multi-tenancy is enforced server-side via the Firebase token + X-Organization-Id header the
 * shared fetch client injects.
 */

import { get, post } from './api';
import type {
  AddCatalogItineraryResult,
  CreateItineraryRequestBody,
  DeclineItineraryRequestBody,
  FulfilItineraryRequestBody,
  ItineraryRequest,
  ItineraryRequestCount,
  ItineraryRequestStatus,
  PagedItineraryCatalog,
} from '../types/itinerary';

/**
 * How a coordinator is browsing the catalog. Every narrowing is optional — with none at all the
 * server returns the first page of the most recently published tournaments.
 */
export interface ItineraryCatalogQuery {
  /** 1-based. */
  page?: number;
  pageSize?: number;
  search?: string;
  /** Supplied together with {@link toIso} or not at all — half a window is not a window. */
  fromIso?: string;
  toIso?: string;
}

/**
 * One page of the shared catalog.
 *
 * With no arguments this returns the most recently published tournaments, newest first — the
 * catalog's job is to show a coordinator what RosterPilot has just built, not to make them guess a
 * search term to see anything at all.
 */
export async function getItineraryCatalog(
  query: ItineraryCatalogQuery = {},
  signal?: AbortSignal
): Promise<PagedItineraryCatalog> {
  const params = new URLSearchParams();
  if (query.page !== undefined) params.set('page', String(query.page));
  if (query.pageSize !== undefined) params.set('pageSize', String(query.pageSize));

  const trimmed = query.search?.trim();
  if (trimmed) params.set('search', trimmed);

  // Both or neither: the server rejects half a window rather than guessing the other end.
  if (query.fromIso && query.toIso) {
    params.set('from', query.fromIso);
    params.set('to', query.toIso);
  }

  const qs = params.toString();
  return get<PagedItineraryCatalog>(
    `/api/itinerary-catalog${qs ? `?${qs}` : ''}`,
    { signal }
  );
}

/**
 * Copy a published itinerary onto the caller org's calendar. Idempotent — re-posting returns the
 * copy the org already holds (`alreadyAdded: true`) instead of creating a second one.
 */
export async function addCatalogItinerary(
  catalogEventId: string,
  signal?: AbortSignal
): Promise<AddCatalogItineraryResult> {
  return post<AddCatalogItineraryResult>(
    `/api/itinerary-catalog/${catalogEventId}/add`,
    undefined,
    { signal }
  );
}

/** The caller org's own itinerary requests, newest first. */
export async function getItineraryRequests(
  status?: ItineraryRequestStatus,
  signal?: AbortSignal
): Promise<ItineraryRequest[]> {
  const params = new URLSearchParams();
  if (status) {
    params.set('status', status);
  }
  const query = params.toString();
  return get<ItineraryRequest[]>(
    `/api/itinerary-requests${query ? `?${query}` : ''}`,
    { signal }
  );
}

/** One of the caller org's own requests. */
export async function getItineraryRequest(
  id: string,
  signal?: AbortSignal
): Promise<ItineraryRequest> {
  return get<ItineraryRequest>(`/api/itinerary-requests/${id}`, { signal });
}

/**
 * Submit an itinerary request. Responds with the tournament name and dates AS RECORDED, which is
 * what the confirmation shows — when the request was raised from an existing calendar event those
 * came from the event, not from anything the coach typed.
 */
export async function createItineraryRequest(
  body: CreateItineraryRequestBody,
  signal?: AbortSignal
): Promise<ItineraryRequest> {
  return post<ItineraryRequest>('/api/itinerary-requests', body, { signal });
}

/** system_admin: the cross-org queue, oldest first. */
export async function getAdminItineraryRequests(
  options: { status?: ItineraryRequestStatus; limit?: number } = {},
  signal?: AbortSignal
): Promise<ItineraryRequest[]> {
  const params = new URLSearchParams();
  if (options.status) {
    params.set('status', options.status);
  }
  if (options.limit != null) {
    params.set('limit', String(options.limit));
  }
  const query = params.toString();
  return get<ItineraryRequest[]>(
    `/api/admin/itinerary-requests${query ? `?${query}` : ''}`,
    { signal }
  );
}

/** system_admin: one request from the queue, in any organization. */
export async function getAdminItineraryRequest(
  id: string,
  signal?: AbortSignal
): Promise<ItineraryRequest> {
  return get<ItineraryRequest>(`/api/admin/itinerary-requests/${id}`, { signal });
}

/** system_admin: close a pending request without building an itinerary. */
export async function declineItineraryRequest(
  id: string,
  resolutionNote?: string,
  signal?: AbortSignal
): Promise<ItineraryRequest> {
  const body: DeclineItineraryRequestBody = {};
  const trimmed = resolutionNote?.trim();
  if (trimmed) {
    body.resolutionNote = trimmed;
  }
  return post<ItineraryRequest>(`/api/admin/itinerary-requests/${id}/decline`, body, { signal });
}

/**
 * system_admin: mark a request fulfilled against an event already on the REQUESTING org's
 * calendar. Manual fallback — sharing a built itinerary normally fulfils the request itself.
 */
export async function fulfilItineraryRequest(
  id: string,
  calendarEventId: string,
  signal?: AbortSignal
): Promise<ItineraryRequest> {
  const body: FulfilItineraryRequestBody = { calendarEventId };
  return post<ItineraryRequest>(`/api/admin/itinerary-requests/${id}/fulfil`, body, { signal });
}

/**
 * How many requests are waiting in RosterPilot's queue, for the admin nav badge (system_admin).
 * A count rather than a page, because it is fetched on every admin page load.
 */
export async function getItineraryRequestCount(
  status: ItineraryRequestStatus = 'Pending',
  signal?: AbortSignal
): Promise<ItineraryRequestCount> {
  return get<ItineraryRequestCount>(
    `/api/admin/itinerary-requests/count?status=${encodeURIComponent(status)}`,
    { signal }
  );
}
